'use client';

import { Suspense, useState, useEffect, useRef } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useSignedInHint } from '@/lib/auth/use-signed-in-hint';
import { Button } from './button';
import { Card, CardHeader, CardContent } from './card';
import { cn } from '@/lib/utils';
import { trackPricingCtaClicked } from '@/components/upgrade/track-paywall-cta';
import { startCheckout, type BillingInterval } from '@/lib/billing/start-checkout';
import { checkoutSignupUrl } from '@/lib/billing/checkout-resume';
import type { Plan } from '@/lib/pricing-data';

interface PricingCardsProps {
  plans: Plan[];
  showHeader?: boolean;
  currentPlanId?: string; // For authenticated users to highlight current plan
  onPlanSelect?: (planId: string) => void; // Optional callback for custom handling
}

/**
 * Public wrapper: PricingCardsInner reads useSearchParams, which bails static
 * prerendering to CSR up to the nearest Suspense boundary. Self-wrapping keeps
 * that bailout scoped to the cards, so pages embedding them (features,
 * pricing-section) keep their static HTML.
 */
export function PricingCards(props: PricingCardsProps) {
  return (
    <Suspense fallback={null}>
      <PricingCardsInner {...props} />
    </Suspense>
  );
}

function PricingCardsInner({
  plans,
  showHeader = false,
  currentPlanId,
  onPlanSelect
}: PricingCardsProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const isSignedIn = useSignedInHint();

  // Default to annual. Monthly-default anchors on the highest recurring number
  // ($79/mo reads worse than $63/mo) and hides the discount entirely unless the
  // visitor happens to toggle. Annual-default shows the better price and the
  // saving up front; the toggle is still one click away.
  // Deep links override: ?interval=monthly flips the toggle off (e.g. the
  // paywall modal's month-to-month escape), ?interval=annual is explicit.
  const [isAnnual, setIsAnnual] = useState(
    () => searchParams.get('interval') !== 'monthly'
  );

  // ?plan= highlights the card a paywall/ad deep-link is selling, so arriving
  // from "Upgrade to Pro" actually acknowledges the choice already made.
  const highlightedPlanId = searchParams.get('plan');
  const [visibleCards, setVisibleCards] = useState<Set<number>>(new Set());
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const [alreadySubscribedMessage, setAlreadySubscribedMessage] = useState<{ message: string; manageUrl?: string } | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.getAttribute('data-index') || '0', 10);
            setVisibleCards((prev) => new Set(prev).add(index));
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px',
      }
    );

    const cards = sectionRef.current?.querySelectorAll('[data-index]');
    cards?.forEach((card) => observer.observe(card));

    return () => {
      cards?.forEach((card) => observer.unobserve(card));
    };
  }, []);

  const calculateAnnualSavings = (monthly: number, annual: number) => {
    return (monthly * 12) - (annual * 12);
  };

  const handlePlanSelect = async (plan: Plan) => {
    // Funnel signal: every plan-card click, on both /pricing and /dashboard/pricing,
    // carrying the origin surface (?from=) so paywall → pricing → checkout is readable.
    const from = new URLSearchParams(window.location.search).get('from') ?? undefined;
    trackPricingCtaClicked({
      cta: 'plan_card',
      plan: plan.id,
      from,
      page: window.location.pathname,
      // Toggle state at click time — without it the annual-first experiment is
      // unreadable (cohort funnels can't segment annual vs monthly intent).
      interval: isAnnual ? 'annual' : 'monthly',
    });

    // If custom handler provided, use it
    if (onPlanSelect) {
      onPlanSelect(plan.id);

      return;
    }

    // Free plan - redirect to dashboard
    if (plan.id === 'free') {
      if (isSignedIn) {
        router.push('/dashboard');
      } else {
        router.push('/sign-in?redirect_url=/dashboard');
      }

      return;
    }

    // Enterprise plan - contact sales
    if (plan.id === 'enterprise') {
      router.push('/contact?topic=sales');

      return;
    }

    setLoadingPlan(plan.id);

    // Promo code from the landing URL (e.g. paywall email → /pricing?promo=WISP20).
    // Read at click time and preserved across the sign-up hop below.
    const promo = new URLSearchParams(window.location.search).get('promo') ?? undefined;
    const interval: BillingInterval = isAnnual ? 'annual' : 'monthly';

    if (!isSignedIn) {
      // Send them to SIGN-UP, not sign-in. Anyone clicking a price is, by
      // definition, likely new — greeting them with "Welcome back" was the
      // single worst step in the funnel. `checkout=1` makes /pricing resume the
      // purchase automatically on return, so the plan choice survives auth and
      // they never have to pick twice. `interval` rides along because the
      // annual/monthly toggle used to be silently lost here too.
      router.push(checkoutSignupUrl({ plan: plan.id, interval, promo, from }));
      setLoadingPlan(null);

      return;
    }

    // Signed in: go straight to checkout from wherever the click happened.
    // (Previously this only worked on /dashboard/pricing; everywhere else it
    // bounced the user to that page and threw the plan choice away.)
    const result = await startCheckout({
      plan: plan.id,
      interval,
      from,
      promo,
      page: window.location.pathname,
    });

    if (result.status === 'already_subscribed') {
      setAlreadySubscribedMessage({ message: result.message, manageUrl: result.manageUrl });
    } else if (result.status === 'error') {
      // Inline, not alert(): an alert is invisible on mobile and reads as a
      // browser error rather than something we own.
      setCheckoutError(result.message);
    }

    if (result.status !== 'redirecting') setLoadingPlan(null);
  };

  return (
    <>
      {checkoutError && (
        <div
          role="alert"
          className="mb-6 rounded-lg border border-red-500/30 bg-red-500/10 p-4 flex flex-wrap items-center justify-between gap-3"
        >
          <p className="text-sm text-[rgb(var(--foreground))]">{checkoutError}</p>
          <Button variant="ghost" size="sm" onClick={() => setCheckoutError(null)} aria-label="Dismiss">
            Dismiss
          </Button>
        </div>
      )}
      {alreadySubscribedMessage && (
        <div className="mb-6 rounded-lg border border-[rgb(var(--primary))]/30 bg-[rgb(var(--primary))]/10 p-4 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-[rgb(var(--foreground))]">
            {alreadySubscribedMessage.message}
          </p>
          <div className="flex items-center gap-2">
            {alreadySubscribedMessage.manageUrl && (
              <a href={alreadySubscribedMessage.manageUrl} target="_blank" rel="noopener noreferrer">
                <Button
                  variant="outline"
                  size="sm"
                >
                  Manage subscription
                </Button>
              </a>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setAlreadySubscribedMessage(null)}
              aria-label="Dismiss"
            >
              Dismiss
            </Button>
          </div>
        </div>
      )}
      {showHeader && (
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-[rgb(var(--foreground))] mb-4">
            Simple, transparent pricing
          </h2>
          <p className="text-lg text-[rgb(var(--foreground-secondary))] max-w-2xl mx-auto mb-8">
            Start free. Upgrade when you need more.
          </p>
        </div>
      )}

      {/* Monthly/Annual Toggle - Only show for paid plans */}
      {plans.some(p => p.monthlyPrice > 0 && p.annualPrice) && (
        <div className="flex items-center justify-center gap-4 mb-12 py-2">
        <span
          className={cn(
            'text-base font-medium transition-colors duration-300',
            !isAnnual
              ? 'text-[rgb(var(--foreground))]'
              : 'text-[rgb(var(--foreground-secondary))]'
          )}
        >
          Monthly
        </span>
        <button
          type="button"
          onClick={() => setIsAnnual(!isAnnual)}
          className="relative inline-flex h-11 w-16 min-h-[44px] items-center rounded-full bg-[rgb(var(--surface))] border border-[rgb(var(--surface-border))] transition-colors focus:outline-none focus:ring-2 focus:ring-[rgb(var(--primary))] focus:ring-offset-2"
          aria-label={isAnnual ? 'Switch to monthly billing' : 'Switch to annual billing'}
        >
          <span
            className={cn(
              'inline-block h-6 w-6 transform rounded-full bg-gradient-to-r from-[#8B5CF6] via-[#EC4899] to-[#F59E0B] transition-transform duration-300',
              isAnnual ? 'translate-x-8' : 'translate-x-1.5'
            )}
          />
        </button>
        <div className="flex items-center gap-2">
          <span
            className={cn(
              'text-base font-medium transition-colors duration-300',
              isAnnual
                ? 'text-[rgb(var(--foreground))]'
                : 'text-[rgb(var(--foreground-secondary))]'
            )}
          >
            Annual
          </span>
          {isAnnual && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-[#8B5CF6]/20 via-[#EC4899]/20 to-[#F59E0B]/20 border border-[#EC4899]/40 text-[#EC4899]">
              Save 20%
            </span>
          )}
        </div>
      </div>
      )}

      <div 
        ref={sectionRef} 
        className={cn(
          // 3-up cards were compressing to ~330px inside max-w-5xl, wrapping
          // "billed annually · $X/yr" and taglines even on desktop. 6xl gives
          // each card ~365px of content width without touching page containers.
          'grid gap-6 md:gap-8 max-w-6xl mx-auto min-w-0',
          plans.length === 1 ? 'grid-cols-1 max-w-md' :
          plans.length === 2 ? 'grid-cols-1 md:grid-cols-2 max-w-4xl' :
          'grid-cols-1 md:grid-cols-3'
        )}
      >
        {plans.map((plan, index) => {
          const isVisible = visibleCards.has(index);
          const isCurrentPlan = currentPlanId === plan.id;
          // Always quote a MONTHLY figure, even on the annual toggle — annual
          // shows the discounted per-month rate ($63/mo) with "billed annually"
          // underneath, not the 12x total ($756). Quoting the total makes the
          // cheaper option look ten times more expensive, which is the opposite
          // of what the toggle is for.
          const price = isAnnual && plan.annualPrice ? plan.annualPrice : plan.monthlyPrice;
          const period = '/month';
          const savings = plan.annualPrice ? calculateAnnualSavings(plan.monthlyPrice, plan.annualPrice) : 0;
          const isEnterprise = plan.id === 'enterprise';
          
          // Handle custom pricing display for Enterprise
          let displayPrice: string;

          if (plan.monthlyPrice === 0) {
            displayPrice = 'Free';
          } else if (plan.isCustomPricing && plan.customPricingLabel) {
            displayPrice = plan.customPricingLabel;
          } else {
            displayPrice = `$${price}`;
          }

          // CTA label. Every card used to read "Get Started", which gave a
          // visitor no reason to pick one over another and no sense of what
          // clicking would do. Name the plan instead — the label is the choice.
          const ctaLabel =
            loadingPlan === plan.id
              ? 'Loading…'
              : isCurrentPlan
                ? 'Current Plan'
                : plan.id === 'enterprise'
                  ? plan.cta
                  : plan.id === 'free'
                    ? isSignedIn
                      ? 'Go to dashboard'
                      : 'Start free'
                    : `Get ${plan.name}`;

          return (
            <div
              key={plan.name}
              data-index={index}
              className={cn(
                'pricing-card relative',
                isVisible ? 'animate-fade-in-up' : 'opacity-0',
                plan.popular && 'md:mt-0 md:-mt-4',
                isEnterprise && 'md:col-span-3' // Enterprise takes full width on desktop
              )}
              style={{
                animationDelay: `${index * 100}ms`,
              }}
            >
              {/* Most Popular Badge - Outside top border */}
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-20">
                  <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-gradient-to-r from-[#8B5CF6] via-[#EC4899] to-[#F59E0B] border-2 border-[rgb(var(--background))] text-white text-xs font-semibold shadow-lg">
                    Most Popular
                  </div>
                </div>
              )}

              <Card
                variant="elevated"
                className={cn(
                  'h-full group hover:scale-[1.02] transition-all duration-300 ease-in-out hover:shadow-2xl relative overflow-hidden',
                  plan.popular && 'border-2 border-[rgb(var(--primary))] md:pt-12 pt-8',
                  isCurrentPlan && 'ring-2 ring-[rgb(var(--primary))] ring-offset-2',
                  // Deep-linked plan (?plan=pro from a paywall or ad CTA) gets the
                  // same selected affordance so the page acknowledges the choice.
                  !isCurrentPlan &&
                    highlightedPlanId === plan.id &&
                    'ring-2 ring-[rgb(var(--primary))] ring-offset-2',
                  isEnterprise && 'md:h-auto' // Allow Enterprise to have custom height
                )}
              >
                {/* Gradient border glow on hover */}
                <div className="absolute -inset-[1px] rounded-xl bg-gradient-to-r from-[#8B5CF6] via-[#EC4899] to-[#F59E0B] opacity-0 group-hover:opacity-20 blur-sm transition-opacity duration-300 -z-10" />

                {/* Enterprise: Horizontal Layout on Desktop */}
                {isEnterprise ? (
                  <div className={cn(
                    'flex flex-col md:flex-row',
                    'p-8'
                  )}>
                    {/* Left Side: Price & Header */}
                    <div className="flex-1 md:pr-8 md:border-r md:border-[rgb(var(--surface-border))] mb-6 md:mb-0">
                      <div className="mb-4">
                        <h3 className="text-2xl font-bold text-[rgb(var(--foreground))] mb-2">
                          {plan.name}
                        </h3>
                        <div className="flex items-baseline gap-2 flex-wrap mb-2">
                          <span className="text-5xl font-bold text-[rgb(var(--foreground))]">
                            {displayPrice}
                          </span>
                          {plan.isCustomPricing && plan.monthlyPrice > 0 && (
                            <span className="text-sm text-[rgb(var(--foreground-secondary))] mt-1">
                              (Starting at ${plan.monthlyPrice}/month)
                            </span>
                          )}
                        </div>
                        {isCurrentPlan && (
                          <div className="mt-2">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[rgb(var(--primary))]/20 text-[rgb(var(--primary))] border border-[rgb(var(--primary))]/40">
                              Current Plan
                            </span>
                          </div>
                        )}
                      </div>
                      <p className="text-base text-[rgb(var(--foreground-secondary))] mb-6">
                        {plan.tagline}
                      </p>
                      <Button
                        variant="secondary"
                        size="md"
                        onClick={() => handlePlanSelect(plan)}
                        disabled={loadingPlan === plan.id || isCurrentPlan}
                        className={cn(
                          'w-full md:w-auto',
                          isCurrentPlan && 'opacity-50 cursor-not-allowed'
                        )}
                      >
                        {ctaLabel}
                      </Button>
                    </div>

                    {/* Right Side: Features */}
                    <div className="flex-1 md:pl-8">
                      <ul className="space-y-4">
                        {plan.features.map((feature, featureIndex) => (
                          <li key={featureIndex} className="flex items-start gap-3">
                            <svg
                              className="w-5 h-5 text-[rgb(var(--success))] mt-0.5 flex-shrink-0"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                            <span className="text-sm text-[rgb(var(--foreground-secondary))] leading-relaxed">
                              {feature}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ) : (
                  /* Standard Vertical Layout for Other Plans */
                  <>
                    <CardHeader className={cn('p-8 pb-6', plan.popular && 'pt-4')}>
                      <div className="flex items-start justify-between gap-4 mb-4">
                        <div className="flex-1">
                          <h3 className="text-2xl font-bold text-[rgb(var(--foreground))] mb-2">
                            {plan.name}
                          </h3>

                          <div className="flex items-baseline gap-2 flex-wrap">
                            <span className="text-5xl font-bold text-[rgb(var(--foreground))]">
                              {displayPrice}
                            </span>
                            {plan.monthlyPrice > 0 && !plan.isCustomPricing && (
                              <span className="text-lg text-[rgb(var(--foreground-secondary))]">
                                {period}
                              </span>
                            )}
                            {plan.isCustomPricing && plan.monthlyPrice > 0 && (
                              <span className="text-sm text-[rgb(var(--foreground-secondary))] mt-1">
                                (Starting at ${plan.monthlyPrice}/month)
                              </span>
                            )}
                          </div>
                          {isAnnual && plan.annualPrice && !plan.isCustomPricing && (
                            <p className="mt-1 text-sm text-[rgb(var(--foreground-secondary))]">
                              billed annually · ${plan.annualPrice * 12}/yr
                            </p>
                          )}
                          {isCurrentPlan && (
                            <div className="mt-2">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[rgb(var(--primary))]/20 text-[rgb(var(--primary))] border border-[rgb(var(--primary))]/40">
                                Current Plan
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Savings Badge - Ecommerce Style */}
                        {isAnnual && plan.annualPrice && savings > 0 && (
                          <div className="flex-shrink-0">
                            <div className="inline-flex items-center px-3 py-1.5 rounded-lg bg-gradient-to-r from-[#8B5CF6] via-[#EC4899] to-[#F59E0B] text-white text-xs font-bold shadow-lg">
                              <span className="relative z-10">Save ${savings}/yr</span>
                            </div>
                          </div>
                        )}
                      </div>

                      <p className="text-base text-[rgb(var(--foreground-secondary))] mb-8">
                        {plan.tagline}
                      </p>
                    </CardHeader>

                    <CardContent className={cn('p-8 pt-0 space-y-6', plan.popular && 'pb-10')}>
                      <ul className="space-y-4">
                        {plan.features.map((feature, featureIndex) => (
                          <li key={featureIndex} className="flex items-start gap-3">
                            <svg
                              className="w-5 h-5 text-[rgb(var(--success))] mt-0.5 flex-shrink-0"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                            <span className="text-sm text-[rgb(var(--foreground-secondary))] leading-relaxed">
                              {feature}
                            </span>
                          </li>
                        ))}
                      </ul>

                      <Button
                        variant={plan.popular ? 'primary' : 'secondary'}
                        size="md"
                        onClick={() => handlePlanSelect(plan)}
                        disabled={loadingPlan === plan.id || isCurrentPlan}
                        className={cn(
                          'w-full',
                          plan.popular &&
                            'bg-gradient-to-r from-[#8B5CF6] via-[#EC4899] to-[#F59E0B] hover:from-[#7C3AED] hover:via-[#DB2777] hover:to-[#D97706] text-white border-0',
                          isCurrentPlan && 'opacity-50 cursor-not-allowed'
                        )}
                      >
                        {ctaLabel}
                      </Button>
                    </CardContent>
                  </>
                )}
              </Card>
            </div>
          );
        })}
      </div>
    </>
  );
}
